// ============================================
// File: src/components/pages/StudentLoginPage.jsx
//
// Student sign-in is available both on the web (phone number → 6-digit OTP)
// and via the DriveZoe mobile app. Both options are offered on this page —
// the app-download section sits alongside the original web sign-in form.
//
// Route suggestion (App router):
//   <Route path="/student-login" element={<StudentLoginPage />} />
//
// On web-login success, navigates to STUDENT_HOME.
//
// Store links are read from env vars so no URL is hardcoded/fabricated here.
// Set these in each environment's .env once the real listing links exist:
//   REACT_APP_IOS_APP_URL      - App Store URL
//   REACT_APP_ANDROID_APP_URL  - Google Play URL
// Until set, the corresponding button is hidden rather than linking nowhere.
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  startStudentLogin,
  confirmStudentOtp,
} from '../../services/cognitoAuth';
import { useStudentAuth } from '../../context/StudentAuthContext';

const STUDENT_HOME = '/dashboard';

const IOS_APP_URL = process.env.REACT_APP_IOS_APP_URL || '';
const ANDROID_APP_URL = process.env.REACT_APP_ANDROID_APP_URL || '';

const card = {
  maxWidth: '26rem',
  width: '100%',
  padding: '2.5rem 2rem',
  backgroundColor: 'white',
  borderRadius: '1rem',
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  textAlign: 'center',
};

const storeButton = {
  display: 'block',
  width: '100%',
  marginTop: '1rem',
  padding: '0.8rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
  border: 'none',
  borderRadius: '0.5rem',
  cursor: 'pointer',
  textDecoration: 'none',
  boxSizing: 'border-box',
};
const input = {
  width: '100%',
  padding: '0.75rem 1rem',
  fontSize: '1rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.5rem',
  boxSizing: 'border-box',
  marginTop: '0.4rem',
};
const button = (disabled) => ({
  width: '100%',
  marginTop: '1.25rem',
  padding: '0.8rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  background: disabled
    ? '#a5b4fc'
    : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
  border: 'none',
  borderRadius: '0.5rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const StudentLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { markStudentLoggedIn } = useStudentAuth();
  const justRegistered = Boolean(location.state?.justRegistered);
  const registeredName = location.state?.name;

  const noStoreLinksConfigured = !IOS_APP_URL && !ANDROID_APP_URL;

  // ── Web sign-in (phone → OTP) — kept as an option alongside the app ──
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const challengeRef = useRef(null);
  const otpInputRef = useRef(null);

  useEffect(() => {
    if (step === 'otp') otpInputRef.current?.focus();
  }, [step]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      challengeRef.current = await startStudentLogin(phone);
      setAttemptsLeft(3);
      setCode('');
      setStep('otp');
    } catch (err) {
      // PreventUserExistenceErrors=ENABLED means unknown phones surface as
      // NotAuthorizedException — show one generic message either way.
      setError(
        'We could not start sign-in with that number. Check the number, or ' +
          'ask your instructor for a registration link if you have not signed up yet.'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await confirmStudentOtp({
        session: challengeRef.current.session,
        username: challengeRef.current.username,
        phoneE164: challengeRef.current.phoneE164,
        code,
      });

      if (result.success) {
        // Tokens are already in localStorage at this point (confirmStudentOtp
        // wrote them), but StudentAuthContext's isStudentAuthenticated state
        // was only read from localStorage ONCE, at provider mount — it has
        // no way to know a login just happened unless told explicitly. Without
        // this call, App.js's route branch check still sees
        // isStudentAuthenticated === false right after navigate() below, and
        // falls through to the public "Please log in to continue" screen.
        markStudentLoggedIn();
        navigate(STUDENT_HOME);
        return;
      }
      if (result.retry) {
        challengeRef.current.session = result.session;
        setAttemptsLeft((n) => n - 1);
        setCode('');
        setError('That code is not right — please try again.');
      }
    } catch (err) {
      // 3 wrong answers → NotAuthorizedException; session is dead
      setError('Too many incorrect attempts. Please request a new code.');
      setStep('phone');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        padding: '2rem 1rem',
      }}
    >
      <div style={card}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.4rem', color: '#111827' }}>
          Student sign in
        </h2>
        <p style={{ margin: '0 0 1.25rem', color: '#6b7280', fontSize: '0.95rem', textAlign: 'left' }}>
          {step === 'phone'
            ? 'Enter the mobile number you registered with and we will text you a sign-in code, or use the DriveZoe app below.'
            : `Enter the 6-digit code sent to ${challengeRef.current?.maskedPhone || 'your phone'}.`}
        </p>

        {justRegistered && (
          <div
            style={{
              background: '#ecfdf5',
              color: '#065f46',
              padding: '0.7rem 0.9rem',
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              marginBottom: '0.75rem',
              textAlign: 'left',
            }}
          >
            {registeredName ? `Welcome, ${registeredName}! ` : ''}
            Your phone number is verified — sign in below or via the app to
            get started.
          </div>
        )}

        {error && (
          <div
            style={{
              background: '#fef2f2',
              color: '#b91c1c',
              padding: '0.7rem 0.9rem',
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              marginBottom: '0.75rem',
              textAlign: 'left',
            }}
          >
            {error}
          </div>
        )}

        {step === 'phone' ? (
          <form onSubmit={handleSendCode} style={{ textAlign: 'left' }}>
            <label style={{ fontSize: '0.9rem', color: '#374151' }}>
              Mobile number
              <input
                style={input}
                type="tel"
                autoComplete="tel"
                placeholder="07123 456789"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </label>
            <button style={button(busy)} disabled={busy} type="submit">
              {busy ? 'Sending code…' : 'Text me a code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} style={{ textAlign: 'left' }}>
            <label style={{ fontSize: '0.9rem', color: '#374151' }}>
              Sign-in code
              <input
                ref={otpInputRef}
                style={{ ...input, letterSpacing: '0.3em', textAlign: 'center' }}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{8}"
                maxLength={8}
                placeholder="••••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </label>
            <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: '#9ca3af' }}>
              {attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} remaining
            </div>
            <button
              style={button(busy || code.length !== 8)}
              disabled={busy || code.length !== 8}
              type="submit"
            >
              {busy ? 'Verifying…' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setError(null);
              }}
              style={{
                width: '100%',
                marginTop: '0.6rem',
                padding: '0.6rem',
                background: 'none',
                border: 'none',
                color: '#4f46e5',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Use a different number / resend
            </button>
          </form>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            margin: '1.5rem 0',
            color: '#9ca3af',
            fontSize: '0.8rem',
          }}
        >
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          <span style={{ padding: '0 0.75rem' }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>

        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', color: '#111827' }}>
          Get the DriveZoe app
        </h3>
        <p style={{ margin: '0 0 0.75rem', color: '#6b7280', fontSize: '0.9rem', textAlign: 'left' }}>
          Download the app to sign in and manage your lessons, bookings and
          progress on the go.
        </p>

        {IOS_APP_URL && (
          <a href={IOS_APP_URL} style={storeButton} target="_blank" rel="noopener noreferrer">
            Download on the App Store
          </a>
        )}

        {ANDROID_APP_URL && (
          <a href={ANDROID_APP_URL} style={storeButton} target="_blank" rel="noopener noreferrer">
            Get it on Google Play
          </a>
        )}

        {noStoreLinksConfigured && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.7rem 0.9rem',
              background: '#fffbeb',
              color: '#92400e',
              borderRadius: '0.5rem',
              fontSize: '0.85rem',
              textAlign: 'left',
            }}
          >
            App store links are not configured yet. Set REACT_APP_IOS_APP_URL
            and/or REACT_APP_ANDROID_APP_URL in this environment's .env file.
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentLoginPage;